import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    Avatar,
    Divider,
    CircularProgress,
    Link,
} from '@mui/material';
import {
    AddCircleOutline,
    EditOutlined,
    DeleteOutline,
    PersonAddOutlined,
    PersonRemoveOutlined,
    SwapHorizOutlined,
    NoteAddOutlined,
    EditNoteOutlined,
    HistoryEdu as HistoryEduOutlined,
    ChatBubbleOutlineOutlined,
    RefreshOutlined,
    Send,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getActivityLog, postManualUpdate } from '../../services/activityLog';

// ── Icon & Colour Map ────────────────────────────────────────────────────────

const EVENT_CONFIG = {
    so_created:             { icon: AddCircleOutline,           colour: '#4caf50' },
    so_updated:             { icon: EditOutlined,               colour: '#9e9e9e' },
    so_deleted:             { icon: DeleteOutline,              colour: '#f44336' },
    so_assigned:            { icon: PersonAddOutlined,          colour: '#2196f3' },
    so_unassigned:          { icon: PersonRemoveOutlined,       colour: '#9e9e9e' },
    so_status_changed:      { icon: SwapHorizOutlined,          colour: '#ff9800' },
    flash_report_created:   { icon: NoteAddOutlined,            colour: '#4caf50' },
    flash_report_updated:   { icon: EditNoteOutlined,           colour: '#9e9e9e' },
    flash_report_revised:   { icon: HistoryEduOutlined,         colour: '#ff9800' },
    flash_report_deleted:   { icon: DeleteOutline,              colour: '#f44336' },
    details_report_created: { icon: NoteAddOutlined,            colour: '#4caf50' },
    details_report_updated: { icon: EditNoteOutlined,           colour: '#9e9e9e' },
    details_report_revised: { icon: HistoryEduOutlined,         colour: '#ff9800' },
    details_report_deleted: { icon: DeleteOutline,              colour: '#f44336' },
    manual_update:          { icon: ChatBubbleOutlineOutlined,  colour: '#3f51b5' },
};

const DEFAULT_CONFIG = { icon: EditOutlined, colour: '#9e9e9e' };

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
    }) + ', ' + d.toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const PAGE_SIZE = 20;

// ── Component ────────────────────────────────────────────────────────────────

const ServiceOrderActivityLog = forwardRef(function ServiceOrderActivityLog(
    { serviceOrderId, isClosed = false },
    ref
) {
    const navigate = useNavigate();
    const [entries, setEntries]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextPage, setNextPage]       = useState(null);
    const [posting, setPosting]         = useState(false);
    const [message, setMessage]         = useState('');

    // ── Fetch ────────────────────────────────────────────────────────────

    const fetchPage = useCallback(async (page = 1, append = false) => {
        try {
            const data = await getActivityLog(serviceOrderId, { page, page_size: PAGE_SIZE });
            const results = data.results || [];
            if (append) {
                // Load more: append older entries not already present
                setEntries((prev) => {
                    const ids = new Set(prev.map((e) => e.id));
                    return [...prev, ...results.filter((e) => !ids.has(e.id))];
                });
            } else {
                // Refresh: prepend any new entries, keep already-loaded older pages
                setEntries((prev) => {
                    if (prev.length === 0) return results;
                    const existingIds = new Set(prev.map((e) => e.id));
                    const newEntries = results.filter((e) => !existingIds.has(e.id));
                    return newEntries.length > 0 ? [...newEntries, ...prev] : prev;
                });
            }
            setNextPage(data.next ? page + 1 : null);
        } catch (err) {
            console.error('Failed to load activity log', err);
        }
    }, [serviceOrderId]);

    // Initial load
    useEffect(() => {
        setLoading(true);
        fetchPage(1).finally(() => setLoading(false));
    }, [fetchPage]);

    // ── Imperative handle — lets the parent call refresh() on demand ──────
    useImperativeHandle(ref, () => ({
        refresh: () => fetchPage(1),
    }), [fetchPage]);

    const handleRefresh = () => {
        setLoading(true);
        fetchPage(1).finally(() => setLoading(false));
    };

    const handleLoadMore = async () => {
        if (!nextPage) return;
        setLoadingMore(true);
        await fetchPage(nextPage, true);
        setLoadingMore(false);
    };

    // ── Post Manual Update ───────────────────────────────────────────────

    const handlePost = async () => {
        if (!message.trim()) return;
        setPosting(true);
        try {
            const newEntry = await postManualUpdate(serviceOrderId, message.trim());
            setEntries((prev) => [newEntry, ...prev]);
            setMessage('');
        } catch (err) {
            console.error('Failed to post update', err);
        } finally {
            setPosting(false);
        }
    };

    // ── Report deep-links ─────────────────────────────────────────────────

    const handleViewReport = (entry) => {
        if (entry.flash_report_id) {
            navigate(`/reports/flash/${entry.flash_report_id}`);
        } else if (entry.details_report_id) {
            navigate(`/reports/details/${entry.details_report_id}`);
        }
    };

    const handleEditReport = (entry) => {
        if (entry.flash_report_id) {
            navigate(`/flash-report/${serviceOrderId}`);
        } else if (entry.details_report_id && entry.details_report_type) {
            navigate(`/detailed-report/${serviceOrderId}/${entry.details_report_type}`);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────

    return (
        <Paper
            elevation={1}
            sx={{
                display: 'flex', flexDirection: 'column',
                height: '100%', maxHeight: 'calc(100vh - 100px)',
                borderRadius: 2, overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    🕐 Activity Log
                </Typography>
                <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                    <RefreshOutlined fontSize="small" />
                </IconButton>
            </Box>

            {/* Timeline */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
                {loading && entries.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No activity yet.
                    </Typography>
                ) : (
                    entries.map((entry) => {
                        const cfg = EVENT_CONFIG[entry.event_type] || DEFAULT_CONFIG;
                        const IconComp = cfg.icon;
                        const revNote = entry.metadata?.revision_note;
                        const hasReport = entry.flash_report_id || entry.details_report_id;

                        return (
                            <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                {/* Avatar / Icon — show user initials if available */}
                                <Avatar
                                    sx={{
                                        width: 32, height: 32,
                                        bgcolor: `${cfg.colour}18`,
                                        color: cfg.colour,
                                        fontSize: 14, fontWeight: 700, mt: 0.25,
                                    }}
                                >
                                    {entry.performed_by_initials && entry.performed_by_initials !== 'SY'
                                        ? entry.performed_by_initials
                                        : <IconComp sx={{ fontSize: 18 }} />}
                                </Avatar>

                                {/* Content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Actor name */}
                                    {entry.performed_by_name && (
                                        <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                            {entry.performed_by_name}
                                        </Typography>
                                    )}
                                    {/* Message — strip the inline " — "note"" suffix when a blockquote will show it */}
                                    <Typography variant="body2" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                                        {revNote
                                            ? entry.message.replace(/\s*—\s*"[^"]*"\s*$/, '')
                                            : entry.message}
                                    </Typography>

                                    {/* Revision note blockquote */}
                                    {revNote && (
                                        <Box
                                            sx={{
                                                mt: 0.5, pl: 1.5, py: 0.5,
                                                borderLeft: '3px solid', borderColor: 'warning.main',
                                                bgcolor: 'action.hover', borderRadius: 1,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                "{revNote}"
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Attachment link */}
                                    {entry.attachment_url && (
                                        <Link
                                            href={entry.attachment_url}
                                            target="_blank"
                                            rel="noopener"
                                            variant="caption"
                                            sx={{ display: 'block', mt: 0.5 }}
                                        >
                                            📎 Attachment
                                        </Link>
                                    )}

                                    {/* Report deep-links */}
                                    {hasReport && (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ textTransform: 'none', py: 0, minHeight: 0, fontSize: 12 }}
                                                onClick={() => handleViewReport(entry)}
                                            >
                                                View Report
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ textTransform: 'none', py: 0, minHeight: 0, fontSize: 12 }}
                                                onClick={() => handleEditReport(entry)}
                                            >
                                                Edit Report
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Timestamp */}
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                        {fmtDate(entry.created_at)}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })
                )}

                {/* Load more */}
                {nextPage && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Button
                            size="small"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            sx={{ textTransform: 'none' }}
                        >
                            {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            Load more
                        </Button>
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Post form */}
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Post an update
                </Typography>
                <TextField
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                    size="small"
                    placeholder="Type your update here..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isClosed || posting}
                    sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        size="small"
                        endIcon={<Send fontSize="small" />}
                        onClick={handlePost}
                        disabled={isClosed || posting || !message.trim()}
                        sx={{ textTransform: 'none' }}
                    >
                        {posting ? 'Posting…' : 'Post'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
});

export default ServiceOrderActivityLog;
